import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useState } from 'react';

export type SupportConversationPresenceStatus = 'active' | 'inactive' | 'left';

interface SupportConversationPresenceState {
  status: SupportConversationPresenceStatus;
  lastSeenAt: string | null;
}

interface UseSupportConversationPresenceOptions {
  conversationId?: string | null;
  enabled?: boolean;
  observedUserId?: string | null;
  trackSelf?: boolean;
}

const DEFAULT_PRESENCE_STATE: SupportConversationPresenceState = {
  status: 'left',
  lastSeenAt: null,
};

function getCurrentActivityState(): Exclude<SupportConversationPresenceStatus, 'left'> {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return 'inactive';
  }

  return document.visibilityState === 'visible' && window.document.hasFocus() ? 'active' : 'inactive';
}

function resolvePresenceState(
  presenceState: Record<string, Array<{ state?: string; last_seen_at?: string }>>,
  observedUserId?: string | null
): SupportConversationPresenceState {
  if (!observedUserId) {
    return DEFAULT_PRESENCE_STATE;
  }

  const entries = Array.isArray(presenceState[observedUserId]) ? presenceState[observedUserId] : [];
  if (entries.length === 0) {
    return DEFAULT_PRESENCE_STATE;
  }

  const lastSeenAt = entries.reduce<string | null>((latest, entry) => {
    if (typeof entry.last_seen_at !== 'string') {
      return latest;
    }

    if (!latest) {
      return entry.last_seen_at;
    }

    return new Date(entry.last_seen_at).getTime() > new Date(latest).getTime() ? entry.last_seen_at : latest;
  }, null);

  return {
    status: entries.some((entry) => entry.state === 'active') ? 'active' : 'inactive',
    lastSeenAt,
  };
}

export function useSupportConversationPresence({
  conversationId,
  enabled = true,
  observedUserId,
  trackSelf = false,
}: UseSupportConversationPresenceOptions) {
  const { user } = useAuth();
  const [presence, setPresence] = useState<SupportConversationPresenceState>(DEFAULT_PRESENCE_STATE);

  useEffect(() => {
    if (!enabled || !conversationId) {
      setPresence(DEFAULT_PRESENCE_STATE);
      return;
    }

    let cancelled = false;
    let subscribed = false;
    const channelKey = trackSelf && user ? user.id : `support-observer-${conversationId}`;
    const channel = supabase.channel(`support-conversation-presence:${conversationId}`, {
      config: {
        presence: {
          key: channelKey,
        },
      },
    });

    const syncObservedPresence = () => {
      if (cancelled) {
        return;
      }

      const nextPresence = resolvePresenceState(
        channel.presenceState() as Record<string, Array<{ state?: string; last_seen_at?: string }>>,
        observedUserId
      );

      setPresence((current) => {
        if (nextPresence.status === 'left' && !nextPresence.lastSeenAt && current.lastSeenAt) {
          return {
            ...nextPresence,
            lastSeenAt: current.lastSeenAt,
          };
        }

        return nextPresence;
      });
    };

    const trackCurrentPresence = async () => {
      if (!trackSelf || !user || !subscribed || cancelled) {
        return;
      }

      await channel.track({
        user_id: user.id,
        state: getCurrentActivityState(),
        last_seen_at: new Date().toISOString(),
      });
    };

    const handleActivityChange = () => {
      void trackCurrentPresence();
    };

    channel
      .on('presence', { event: 'sync' }, syncObservedPresence)
      .on('presence', { event: 'join' }, syncObservedPresence)
      .on('presence', { event: 'leave' }, syncObservedPresence)
      .subscribe(async (status) => {
        if (status !== 'SUBSCRIBED' || cancelled) {
          return;
        }

        subscribed = true;
        await trackCurrentPresence();
        syncObservedPresence();
      });

    if (trackSelf) {
      window.addEventListener('focus', handleActivityChange);
      window.addEventListener('blur', handleActivityChange);
      window.addEventListener('online', handleActivityChange);
      document.addEventListener('visibilitychange', handleActivityChange);
      window.addEventListener('pagehide', handleActivityChange);
    }

    return () => {
      cancelled = true;
      if (trackSelf) {
        window.removeEventListener('focus', handleActivityChange);
        window.removeEventListener('blur', handleActivityChange);
        window.removeEventListener('online', handleActivityChange);
        document.removeEventListener('visibilitychange', handleActivityChange);
        window.removeEventListener('pagehide', handleActivityChange);
      }
      void channel.untrack().catch(() => undefined);
      void supabase.removeChannel(channel);
    };

  }, [conversationId, enabled, observedUserId, trackSelf, user]);

  return presence;
}
